import React, { useState } from 'react';
import classNames from 'classnames';
import './ChartEntryFilterRow.less';
import { IListItemControlButtonsProps } from 'components/lists/ListItemControlButtons';
import { LIST_ITEM_CLASS_NAME, ListItem } from 'components/lists/ListItem';
import { IconButton } from 'components/buttons/IconButton';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';
import { SimpleSwitch } from 'components/inputs/SimpleSwitch/SimpleSwitch';
import { isNotEmpty } from 'utils/ArrayUtils';
import { ChartEntryMetadata } from '@decisions/server/DecisionsFramework/Design/Charts/ChartEntryMetadata';
import { ChartMetadataType } from '@decisions/server/DecisionsFramework/Design/Charts/ChartMetadataType';
import { Checkbox } from 'components/inputs/Checkbox';
import { ColorSwatch } from 'components/inputs/color-pickers/ColorSwatch';
import { showLegacyColorPickerDialog } from 'components/inputs/color-pickers/LegacyColorPicker';
import { getColorCssString } from 'utils/ColorUtils';
import { DesignerColor } from '@decisions/server/Decisions/Silverlight/UI/Utilities';

interface IChartEntryFilterRowProps extends IListItemControlButtonsProps {
  entry: ChartEntryMetadata;
  selected?: boolean;
  onSelection: (index: number, isMulti: boolean) => void;
  onLabelChange: (index: number, label: string) => void;
  onToggleVisible: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  unGroupChild?: (groupKey: string, index: number) => void;
  onAddCancel: () => void;
  multiSelecting?: boolean;
  isFirst: boolean;
  isLast: boolean;
  onColorChange?: (index: number, color: string) => void;
}

const CHART_FILTER_ENTRY_ROW_CLASS = 'dp-chart-entry-filter-row';
const getClassName = (selected?: boolean) =>
  classNames(CHART_FILTER_ENTRY_ROW_CLASS, {
    'dp-chart-entry-filter-row--selected': selected,
  });

const getMoveUpClass = (isFirst?: boolean) =>
  classNames(`${LIST_ITEM_CLASS_NAME}__up`, {
    'dp-chart-entry-filter-row__button--disabled': isFirst,
  });
const getMoveDownClass = (isLast?: boolean) =>
  classNames(`${LIST_ITEM_CLASS_NAME}__down`, {
    'dp-chart-entry-filter-row__button--disabled': isLast,
  });

/**
 * ChartEntryFilter entry row component.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ChartEntryFilterRow({
  entry,
  index,
  isFirst,
  isLast,
  multiSelecting,
  onAddCancel,
  onColorChange,
  onMoveUp,
  onMoveDown,
  onLabelChange,
  onRemove,
  onSelection,
  onToggleVisible,
  selected,
  unGroupChild: onUnGroup,
}: IChartEntryFilterRowProps) {
  const { Key, LabelText, Visible, Type, Color } = entry;
  const [editingLabel, setEditingLabel] = useState(LabelText === '');
  const [newLabel, setNewLabel] = useState('');
  const isGroup = isNotEmpty(entry.Grouped);

  const beginLabelEdit = () => {
    setNewLabel(LabelText);
    setEditingLabel(true);
  };
  const onLabelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLabel(e.target.value);
  };

  const handleRemove =
    Type === ChartMetadataType.UserDefined
      ? (e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove?.(index);
        }
      : undefined;
  const handleMoveUp = () => !isFirst && onMoveUp(index);
  const handleMoveDown = () => !isLast && onMoveDown(index);

  const onEditFocus = (_e: React.FocusEvent) => {
    setNewLabel(LabelText);
  };
  const onEditBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!Key && !e.target.value) {
      // with no truthy key assigned, this is an add cancel
      return onAddCancel();
    }
    onLabelChange(index, e.target.value);
    setEditingLabel(false);
  };

  const keyboardHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        onLabelChange(index, newLabel);
        setEditingLabel(false);
        break;
      case 'Escape':
        setEditingLabel(false);
        entry.Key === '' && onAddCancel();
        break;
    }
  };

  const { focusTrackingProps } = useTrackFocus({ onFocus: onEditFocus, onBlur: onEditBlur });

  const onSelectionClick = (e: React.MouseEvent) => {
    onSelection(index, e.ctrlKey || !!multiSelecting);
  };

  const onRootClick = (e: React.MouseEvent) => {
    onSelectionClick(e);
  };

  const toggleVisibility = () => {
    onToggleVisible(index);
  };

  const unGroup = (i: number) => {
    onUnGroup && onUnGroup(entry.Key, i);
  };

  const showColorPicker = (e: React.MouseEvent) => {
    showLegacyColorPickerDialog({
      stickTo: e.target as HTMLElement,
      onOk: (designerColor: DesignerColor) => {
        const color = getColorCssString(designerColor);
        onColorChange?.(index, color);
      },
      // onClosed: () => {},
      onClear: () => {
        onColorChange?.(index, '');
      },
      pickedColor: Color,
    });
  };

  return (
    <>
      <div className={getClassName(selected)} onClick={onRootClick}>
        {multiSelecting && (
          <Checkbox
            id={LabelText}
            checked={selected}
            disabled={isGroup}
            onChange={() => {
              /* NOOP */
            }}
            className="dp-chart-entry-filter-row__checkbox"
          />
        )}
        {!multiSelecting && (
          <SimpleSwitch on={Visible} onToggle={toggleVisibility} className="dp-chart-entry-filter-row__visible" />
        )}
        {!editingLabel && (
          <div className="dp-chart-entry-filter-row__label">
            <span className="dp-chart-entry-filter-row__label-text" title={LabelText}>
              {LabelText}&nbsp;
            </span>
            {!multiSelecting && (
              <IconButton
                title="Edit"
                className="dp-chart-entry-filter-row__label-edit-button"
                iconClassName={`${LIST_ITEM_CLASS_NAME}__edit`}
                onClick={beginLabelEdit}
                iconSize="small"
              />
            )}
          </div>
        )}
        {editingLabel && (
          <input
            className="dp-chart-entry-filter-row__label-editor dpc-text-input dpc-text-input-wrapper--small"
            value={newLabel}
            onChange={onLabelInputChange}
            autoFocus={true}
            onKeyUp={keyboardHandler}
            {...focusTrackingProps}
          />
        )}
        {/* {!handleRemove && <IconButton className="dp-chart-entry-filter-row__icon-button-spacer" iconClassName="" />} */}
        {!multiSelecting && handleRemove && (
          <IconButton
            title="Remove"
            className="dp-chart-entry-filter-row__button"
            iconClassName={`${LIST_ITEM_CLASS_NAME}__remove`}
            onClick={handleRemove}
            iconSize="small"
          />
        )}
        {!multiSelecting && (
          <>
            <IconButton
              title="Move Up"
              className="dp-chart-entry-filter-row__button"
              iconClassName={getMoveUpClass(isFirst)}
              onClick={handleMoveUp}
              iconSize="small"
              disabled={isFirst}
            />
            <IconButton
              title="Move Down"
              className="dp-chart-entry-filter-row__button"
              iconClassName={getMoveDownClass(isLast)}
              onClick={handleMoveDown}
              iconSize="small"
              disabled={isLast}
            />
          </>
        )}
        {!multiSelecting && onColorChange && (
          <ColorSwatch
            color={entry.Color}
            size="small"
            className="dp-chart-entry-filter-row__color"
            onClick={showColorPicker}
          />
        )}
      </div>
      {/* if this one is a group, render it's child rows */}
      {isGroup && (
        <div className="dp-chart-entry-filter-row__group">
          {entry.Grouped?.map((groupedEntry, i) => (
            <ListItem
              className="dp-chart-entry-filter-row--grouped"
              key={groupedEntry.Key}
              index={i}
              label={groupedEntry.LabelText}
              onRemove={unGroup}
            />
          ))}
        </div>
      )}
    </>
  );
}
